import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  automationService,
  type Scenario,
  type StrategyTrade,
  type TradeEvent,
} from '@/services/strategyService';
import { EquityCurveChart } from './PerformanceCharts';
import { TradeChartDialog } from '@/components/trading/TradeChartDialog';
import { ShareScenarioDialog } from '@/components/trade-viewer/ShareScenarioDialog';
import { Pagination, usePagedList, useDebounced } from '@/components/ui/pagination';
import { num, duration } from '@/lib/format';
import { SearchInput, FilterChips, SortableHeader, EmptyState, type FilterOption } from '@/components/ui/list-toolbar';
import { ArrowLeft, RefreshCw, Share2 } from 'lucide-react';

interface Bucket { label: string; pnl: number; count: number; }

// Up/down bars around a zero baseline — net PnL per time bucket (green up / red down),
// height ∝ |pnl|. Entry count + PnL in the hover title. Matches the MT5 "P&L by …" charts.
const BucketBars: React.FC<{ title: string; buckets: Bucket[] }> = ({ title, buckets }) => {
  const maxAbs = Math.max(1, ...buckets.map(b => Math.abs(b.pnl)));
  return (
    <div>
      <div className="text-[11px] font-medium mb-1">{title}</div>
      <div className="relative h-24 flex items-stretch gap-[2px]">
        <div className="absolute left-0 right-0 top-1/2 h-px bg-border" />
        {buckets.map((b, i) => {
          const h = (Math.abs(b.pnl) / maxAbs) * 50;
          const pos = b.pnl >= 0;
          return (
            <div key={i} className="flex-1 relative"
              title={`${b.label}: ${b.pnl >= 0 ? '+' : ''}${Math.round(b.pnl).toLocaleString()} · ${b.count} ${b.count === 1 ? 'entry' : 'entries'}`}>
              {b.count > 0 && (
                <div className={`absolute left-0 right-0 rounded-sm ${pos ? 'bg-emerald-500/70' : 'bg-red-500/70'}`}
                  style={pos ? { bottom: '50%', height: `${Math.max(h, 2)}%` } : { top: '50%', height: `${Math.max(h, 2)}%` }} />
              )}
            </div>
          );
        })}
      </div>
      <div className="flex gap-[2px] mt-0.5">
        {buckets.map((b, i) => <div key={i} className="flex-1 text-center text-[8px] text-muted-foreground truncate">{b.label}</div>)}
      </div>
    </div>
  );
};

interface Props {
  /** Authenticated view — the owner opening their own run (bookmarkable `?run=`). */
  runId?: string;
  /** Public view — an expiring share token resolved via the anonymous `by-share` endpoint. */
  shareToken?: string;
  /** Authenticated back-navigation (returns to the lab). Omitted in public mode. */
  onBack?: () => void;
}

/**
 * Rich, self-contained backtest run detail — stat grid + equity curve + best/worst
 * + the full (lazy) trade list + per-trade chart drill-down. Used in TWO places:
 *
 *  - Authenticated, bookmarkable: <BacktestRunDetail runId=… onBack=… /> inside the
 *    Backtest Lab (the `?run=<id>` deep link). Trades load on demand; a Share button
 *    mints an expiring public link.
 *  - Public, read-only: <BacktestRunDetail shareToken=… /> behind no auth gate
 *    (#/backtest-run?key=<token>). The by-share endpoint returns the full scenario
 *    incl. trades, so nothing is lazy here. The per-trade candle chart needs
 *    authenticated bar data, so it's disabled for anonymous viewers.
 */
export const BacktestRunDetail: React.FC<Props> = ({ runId, shareToken, onBack }) => {
  const isPublic = !!shareToken;
  const [detail, setDetail] = useState<Scenario | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Lazy trade list (authed). In public mode the by-share payload already carries trades.
  const [trades, setTrades] = useState<StrategyTrade[] | null>(null);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [tradesError, setTradesError] = useState<string | null>(null);

  // Table view state. The trades array is already fully loaded (the time-bucket
  // aggregates above need every row), so this filters and pages the set in hand
  // rather than re-querying — and `total` below is still the whole FILTERED count,
  // which is what makes the range readout mean the same thing as everywhere else.
  const [tradeSearch, setTradeSearch] = useState('');
  const [tradeOutcome, setTradeOutcome] = useState<TradeOutcomeFilter>('all');
  const [tradeSort, setTradeSort] = useState<{ key: TradeCol; dir: 'asc' | 'desc' }>(
    { key: 'entryTime', dir: 'asc' });

  const debouncedTradeSearch = useDebounced(tradeSearch, 250);
  const {
    page: tradePage, setPage: setTradePage,
    pageSize: tradePageSize, setPageSize: setTradePageSize,
  } = usePagedList({
    pageSize: 50,
    resetOn: [debouncedTradeSearch, tradeOutcome, tradeSort.key, tradeSort.dir],
  });
  // Per-trade chart drill-down (authed only — bars need auth).
  const [chartTrade, setChartTrade] = useState<{ trade: any; events: TradeEvent[]; entryReason: string | null } | null>(null);
  const [shareOpen, setShareOpen] = useState(false);

  // Race guard: the identity we're currently fetching for (runId or token).
  const keyRef = useRef<string | null>(null);

  useEffect(() => {
    const key = shareToken ?? runId ?? null;
    keyRef.current = key;
    if (!key) { setLoading(false); setLoadError('No run specified.'); return; }
    setLoading(true); setLoadError(null); setDetail(null);
    setTrades(null); setTradesError(null);
    (async () => {
      try {
        // Public: full scenario (incl. trades) in one anonymous-safe call.
        // Authed: lightweight (stats + equity + best/worst); the heavy trade list loads on demand.
        const s = shareToken
          ? await automationService.getScenarioByShare(shareToken)
          : await automationService.getScenario(runId!, false);
        if (keyRef.current !== key) return; // a newer run replaced us
        setDetail(s);
        // Public payload already has the trades — surface them without a second fetch.
        if (shareToken && Array.isArray(s.trades)) setTrades(s.trades);
      } catch (e: any) {
        if (keyRef.current === key) setLoadError(isPublic
          ? 'This share link is invalid or has expired.'
          : "Couldn't load this run (it may have been pruned).");
      } finally {
        if (keyRef.current === key) setLoading(false);
      }
    })();
  }, [runId, shareToken, isPublic]);

  const loadTrades = useCallback(async () => {
    if (isPublic || !runId) return;
    setTradesLoading(true); setTradesError(null);
    try {
      const t = await automationService.getScenarioTrades(runId);
      if (keyRef.current === runId) setTrades(t);
    } catch { setTradesError('Failed to load trades for this run.'); }
    finally { setTradesLoading(false); }
  }, [isPublic, runId]);

  // Open a single trade on a price chart. Works in the public view too — the chart fetches bars
  // via the anonymous token-scoped endpoint. assetClass comes from the run snapshot (shared
  // payload keeps AssetClass) so forex/equity pick the right bar source + precision.
  const openTradeChart = useCallback((t: StrategyTrade) => {
    // The trade's own write-once assetClass wins; the snapshot covers pre-field
    // legacy trades (editing the strategy later must not re-render old history).
    const assetClass = t.assetClass ?? (detail?.strategySnapshot as any)?.assetClass ?? 'Equity';
    setChartTrade({ trade: { ...t, assetClass }, events: t.events ?? [], entryReason: t.entryReason ?? null });
  }, [detail]);

  const stats = detail?.statistics ?? null;
  const tradeClickable = true;
  // Starting account size — the equity curve is startingEquity + cumulativePnL, so the base is
  // equity − cumulativePnL at any point (constant). Derive it from the first point.
  const startCapital = stats?.equityCurve?.length
    ? stats.equityCurve[0].equity - stats.equityCurve[0].cumulativePnL
    : null;

  // "When it traded" buckets — net PnL + entry count by weekday / hour / month, from the trade
  // list (loaded on demand in the authed view; present in the public payload).
  const timeBuckets = useMemo(() => {
    const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const wd: Bucket[] = WD.map(label => ({ label, pnl: 0, count: 0 }));
    const hr: Bucket[] = Array.from({ length: 24 }, (_, i) => ({ label: String(i), pnl: 0, count: 0 }));
    const mo: Bucket[] = MO.map(label => ({ label, pnl: 0, count: 0 }));
    for (const t of trades ?? []) {
      if (!t.entryTime) continue;
      const d = new Date(t.entryTime);
      const p = t.realizedPnL ?? 0;
      wd[d.getDay()].pnl += p; wd[d.getDay()].count++;
      hr[d.getHours()].pnl += p; hr[d.getHours()].count++;
      mo[d.getMonth()].pnl += p; mo[d.getMonth()].count++;
    }
    return { wd, hr, mo };
  }, [trades]);

  // Filter → sort → page, in that order. Doing it the other way round would filter
  // only the rows on the current page, which looks identical and answers a different
  // question.
  const visibleTrades = useMemo(() => {
    let xs = trades ?? [];
    const q = debouncedTradeSearch.trim().toLowerCase();
    if (q) {
      xs = xs.filter(t =>
        t.symbol.toLowerCase().includes(q)
        || (t.exitReason ?? '').toLowerCase().includes(q)
        || (t.entryReason ?? '').toLowerCase().includes(q));
    }
    if (tradeOutcome === 'win') xs = xs.filter(t => (t.realizedPnL ?? 0) > 0);
    else if (tradeOutcome === 'loss') xs = xs.filter(t => (t.realizedPnL ?? 0) < 0);

    const dir = tradeSort.dir === 'asc' ? 1 : -1;
    const key = tradeSort.key;
    return [...xs].sort((a, b) => {
      const av = (a as any)[key];
      const bv = (b as any)[key];
      // Nulls last in both directions: a missing exit price is "no answer", not the
      // smallest one.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return av < bv ? -dir : av > bv ? dir : 0;
    });
  }, [trades, debouncedTradeSearch, tradeOutcome, tradeSort]);

  const pagedTrades = useMemo(
    () => visibleTrades.slice((tradePage - 1) * tradePageSize, tradePage * tradePageSize),
    [visibleTrades, tradePage, tradePageSize],
  );

  const toggleTradeSort = (key: TradeCol) =>
    setTradeSort(prev => (prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }));

  return (
    <div className="space-y-4">
      {/* Header: title + back/share */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack} className="px-2">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            <h2 className="text-base font-semibold truncate">
              {detail?.name || (loading ? 'Loading run…' : 'Run detail')}
            </h2>
          </div>
          {detail && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {detail.strategySnapshot?.assetClass || 'Equity'}
              {detail.symbols?.length ? ` · ${detail.symbols.length} symbol${detail.symbols.length === 1 ? '' : 's'}` : ''}
              {detail.createdAt ? ` · ${new Date(detail.createdAt).toLocaleString()}` : ''}
              {isPublic ? ' · shared (read-only)' : ''}
            </div>
          )}
        </div>
        {!isPublic && runId && (
          <Button variant="outline" size="sm" onClick={() => setShareOpen(true)}>
            <Share2 className="h-4 w-4 mr-1" /> Share
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading run…</div>
      ) : loadError ? (
        <div className="text-sm text-red-500">{loadError}</div>
      ) : !stats ? (
        <div className="text-sm text-muted-foreground">
          No statistics for this run{detail?.error ? ` — ${detail.error}` : ' (0 trades).'}
        </div>
      ) : (
        <>
          {/* Stat grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 text-sm">
            {([
              ['Start capital', startCapital != null ? num(startCapital, 0) : '—'],
              ['Trades', num(stats.totalTrades, 0)],
              ['Net PnL', num(stats.totalPnL, 0)],
              ['Return %', num(stats.totalPnLPercent, 1)],
              ['Win%', num(stats.winRate, 1)],
              ['Profit factor', num(stats.profitFactor, 2)],
              ['Avg time in market', duration(stats.avgHoldingSeconds)],
              ['Avg R', num(stats.avgRMultiple, 2)],
              ['Expectancy', num(stats.expectancy, 2)],
              ['Max DD%', num(stats.maxDrawdownPercent, 1)],
              ['Sharpe (ann.)', num(stats.sharpeRatio, 2)],
              ['Recovery', num(stats.recoveryFactor, 2)],
              ['Curve fit (LR)', num(stats.lrCorrelation, 2)],
              ['Gross P / L', `${num(stats.grossProfit, 0)} / ${num(stats.grossLoss, 0)}`],
              ['Avg win', num(stats.avgWin, 0)],
              ['Avg loss', num(stats.avgLoss, 0)],
              ['Largest win', num(stats.largestWin, 0)],
              ['Largest loss', num(stats.largestLoss, 0)],
              ['Winners / Losers', `${stats.winners} / ${stats.losers}`],
              ['Max win streak', `${stats.maxConsecutiveWins ?? 0} (${num(stats.maxConsecutiveWinsProfit, 0)})`],
              ['Max loss streak', `${stats.maxConsecutiveLosses ?? 0} (${num(stats.maxConsecutiveLossesAmount, 0)})`],
            ] as [string, string][]).map(([label, value]) => (
              <div key={label}>
                <div className="text-muted-foreground text-xs">{label}</div>
                <div className="tabular-nums">{value}</div>
              </div>
            ))}
          </div>

          {/* Equity curve */}
          {stats.equityCurve?.length > 0 && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Equity curve</div>
              <EquityCurveChart equityCurve={stats.equityCurve} />
            </div>
          )}

          {/* Long vs Short breakdown */}
          <div>
            <div className="text-xs text-muted-foreground mb-1">Long vs Short</div>
            <table className="w-full max-w-md text-xs">
              <thead className="text-left text-muted-foreground border-b">
                <tr><th className="px-2 py-1">Direction</th><th className="px-2 py-1 text-right">Trades</th><th className="px-2 py-1 text-right">Win%</th><th className="px-2 py-1 text-right">Net PnL</th></tr>
              </thead>
              <tbody>
                {([['Long', stats.longTrades, stats.longWins, stats.longPnL], ['Short', stats.shortTrades, stats.shortWins, stats.shortPnL]] as const).map(([dir, n, w, p]) => (
                  <tr key={dir} className="border-b border-border/30">
                    <td className="px-2 py-1">{dir}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{n ?? 0}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{n ? num(((w ?? 0) / n) * 100, 1) : '—'}</td>
                    <td className={`px-2 py-1 text-right tabular-nums ${(p ?? 0) > 0 ? 'text-emerald-500' : (p ?? 0) < 0 ? 'text-red-500' : ''}`}>{num(p, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* When it traded — PnL by weekday / hour / month (needs the trade list) */}
          {trades && trades.length > 0 ? (
            <div>
              <div className="text-xs text-muted-foreground mb-2">When it traded — net PnL by bucket (green up / red down; hover for entries)</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <BucketBars title="Weekday" buckets={timeBuckets.wd} />
                <BucketBars title="Hour of day (local)" buckets={timeBuckets.hr} />
                <BucketBars title="Month" buckets={timeBuckets.mo} />
              </div>
            </div>
          ) : !isPublic ? (
            <div className="text-xs text-muted-foreground">Load the trades below to see net PnL by weekday / hour / month.</div>
          ) : null}

          {/* Best / worst trades */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {([['Best trades', stats.bestTrades], ['Worst trades', stats.worstTrades]] as const).map(([label, list]) => (
              <div key={label}>
                <div className="text-xs text-muted-foreground mb-1">{label}</div>
                <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-muted-foreground border-b">
                    <tr><th className="px-2 py-1">Symbol</th><th className="px-2 py-1 text-right">PnL</th><th className="px-2 py-1 text-right">R</th><th className="px-2 py-1">Exit</th></tr>
                  </thead>
                  <tbody>
                    {(list ?? []).map(t => (
                      <tr key={t.id} className={`border-b border-border/30 ${tradeClickable ? 'hover:bg-muted/30 cursor-pointer' : ''}`}
                        onClick={tradeClickable ? () => openTradeChart(t) : undefined}
                        title={tradeClickable ? 'Open this trade on a chart' : undefined}>
                        <td className="px-2 py-1">{t.symbol}</td>
                        <td className={`px-2 py-1 text-right tabular-nums ${t.realizedPnL > 0 ? 'text-emerald-500' : t.realizedPnL < 0 ? 'text-red-500' : ''}`}>{num(t.realizedPnL, 0)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{num(t.realizedRMultiple, 2)}</td>
                        <td className="px-2 py-1 text-muted-foreground truncate max-w-[120px]" title={t.exitReason ?? ''}>{t.exitReason ?? '—'}</td>
                      </tr>
                    ))}
                    {(list ?? []).length === 0 && <tr><td colSpan={4} className="px-2 py-1 text-muted-foreground">—</td></tr>}
                  </tbody>
                </table>
                </div>
              </div>
            ))}
          </div>

          {/* Full trade list — loaded ON DEMAND (the heavy part) in authed mode; already present in public mode. */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs text-muted-foreground">
                All trades{trades
                  ? visibleTrades.length === trades.length
                    ? ` (${trades.length.toLocaleString()})`
                    : ` (${visibleTrades.length.toLocaleString()} of ${trades.length.toLocaleString()})`
                  : ` (${stats.totalTrades.toLocaleString()})`}
              </div>
              {!isPublic && trades === null && (
                <Button variant="outline" size="sm" onClick={loadTrades} disabled={tradesLoading}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${tradesLoading ? 'animate-spin' : ''}`} />
                  Load trades
                </Button>
              )}
            </div>
            {tradesError && <div className="text-xs text-red-500 mb-1">{tradesError}</div>}
            {trades && (
              trades.length === 0 ? <div className="text-xs text-muted-foreground">No trades.</div> : (
                <div className="rounded-md border">
                  <div className="flex flex-wrap items-center gap-2 border-b p-2">
                    <SearchInput
                      value={tradeSearch}
                      onChange={setTradeSearch}
                      placeholder="Symbol or reason…"
                      className="w-44"
                    />
                    <FilterChips
                      value={tradeOutcome}
                      options={RUN_OUTCOME_OPTIONS}
                      onChange={(v) => setTradeOutcome(v)}
                    />
                    <div className="ml-auto">
                      <Pagination
                        page={tradePage}
                        pageSize={tradePageSize}
                        total={visibleTrades.length}
                        onPageChange={setTradePage}
                        compact
                        itemLabel="trades"
                      />
                    </div>
                  </div>

                  {visibleTrades.length === 0 ? (
                    <EmptyState
                      title="No trades match these filters"
                      hint="Filters apply to the whole run, not just the visible page."
                    />
                  ) : (
                  <div className="max-h-80 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="text-left text-muted-foreground border-b sticky top-0 bg-card">
                      <tr>
                        <th className="px-2 py-1"><SortableHeader column="symbol" label="Symbol" sortBy={tradeSort.key} sortDir={tradeSort.dir} onSort={toggleTradeSort} /></th>
                        <th className="px-2 py-1"><SortableHeader column="direction" label="Dir" sortBy={tradeSort.key} sortDir={tradeSort.dir} onSort={toggleTradeSort} /></th>
                        <th className="px-2 py-1"><SortableHeader column="entryTime" label="Entry time" sortBy={tradeSort.key} sortDir={tradeSort.dir} onSort={toggleTradeSort} /></th>
                        <th className="px-2 py-1 text-right"><SortableHeader column="entryPrice" label="Entry" sortBy={tradeSort.key} sortDir={tradeSort.dir} onSort={toggleTradeSort} align="right" /></th>
                        <th className="px-2 py-1 text-right"><SortableHeader column="exitPrice" label="Exit" sortBy={tradeSort.key} sortDir={tradeSort.dir} onSort={toggleTradeSort} align="right" /></th>
                        <th className="px-2 py-1 text-right"><SortableHeader column="realizedPnL" label="PnL" sortBy={tradeSort.key} sortDir={tradeSort.dir} onSort={toggleTradeSort} align="right" /></th>
                        <th className="px-2 py-1 text-right"><SortableHeader column="realizedRMultiple" label="R" sortBy={tradeSort.key} sortDir={tradeSort.dir} onSort={toggleTradeSort} align="right" /></th>
                        <th className="px-2 py-1"><SortableHeader column="exitReason" label="Exit reason" sortBy={tradeSort.key} sortDir={tradeSort.dir} onSort={toggleTradeSort} /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedTrades.map(t => (
                        <tr key={t.id} className={`border-b border-border/30 ${tradeClickable ? 'hover:bg-muted/30 cursor-pointer' : ''}`}
                          onClick={tradeClickable ? () => openTradeChart(t) : undefined}
                          title={tradeClickable ? 'Open this trade on a chart' : undefined}>
                          <td className="px-2 py-1 font-medium">{t.symbol}</td>
                          <td className="px-2 py-1">{t.direction === 'Long' ? '▲' : '▼'}</td>
                          <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">{t.entryTime ? new Date(t.entryTime).toLocaleString() : '—'}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{num(t.entryPrice, 2)}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{t.exitPrice ? num(t.exitPrice, 2) : '—'}</td>
                          <td className={`px-2 py-1 text-right tabular-nums ${t.realizedPnL > 0 ? 'text-emerald-500' : t.realizedPnL < 0 ? 'text-red-500' : ''}`}>{num(t.realizedPnL, 0)}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{num(t.realizedRMultiple, 2)}</td>
                          <td className="px-2 py-1 text-muted-foreground truncate max-w-[140px]" title={t.exitReason ?? ''}>{t.exitReason ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  )}

                  <div className="border-t p-2">
                    <Pagination
                      page={tradePage}
                      pageSize={tradePageSize}
                      total={visibleTrades.length}
                      onPageChange={setTradePage}
                      onPageSizeChange={setTradePageSize}
                      itemLabel="trades"
                    />
                  </div>
                </div>
              )
            )}
          </div>
        </>
      )}

      {/* Per-trade chart drill-down — works in the public view via the token-scoped bar endpoint. */}
      <TradeChartDialog
        trade={chartTrade?.trade ?? null}
        events={chartTrade?.events}
        entryReason={chartTrade?.entryReason}
        shareToken={shareToken}
        onClose={() => setChartTrade(null)}
      />

      {/* Share dialog — mints an expiring public link to #/backtest-run?key=<token>. */}
      {!isPublic && runId && (
        <ShareScenarioDialog
          runId={runId}
          scenarioName={detail?.name ?? ''}
          sharePath="backtest-run"
          open={shareOpen}
          onOpenChange={setShareOpen}
        />
      )}
    </div>
  );
};

type TradeCol =
  | 'symbol' | 'direction' | 'entryTime' | 'entryPrice' | 'exitPrice'
  | 'realizedPnL' | 'realizedRMultiple' | 'exitReason';

type TradeOutcomeFilter = 'all' | 'win' | 'loss';

const RUN_OUTCOME_OPTIONS: FilterOption<TradeOutcomeFilter>[] = [
  { value: 'all', label: 'All' },
  { value: 'win', label: 'Wins', activeClass: 'bg-emerald-500 text-white' },
  { value: 'loss', label: 'Losses', activeClass: 'bg-red-500 text-white' },
];

export default BacktestRunDetail;
